import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import violationDetectionService from '../../../services/violationDetectionService';
import { openaiComplianceService } from '../../../services/openaiComplianceService';

const EnhancedRemediationWizard = ({ violation, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);
  const [selectedActions, setSelectedActions] = useState([]);
  const [validationResults, setValidationResults] = useState(null);

  const steps = [
    {
      title: 'AI Analysis',
      description: 'Generate intelligent fix suggestions',
      icon: 'Brain'
    },
    {
      title: 'Review Suggestions',
      description: 'Select remediation actions',
      icon: 'FileSearch'
    },
    {
      title: 'Validate & Approve',
      description: 'Confirm correction details',
      icon: 'CheckSquare'
    },
    {
      title: 'Execute Remediation',
      description: 'Apply automated corrections',
      icon: 'Zap'
    },
    {
      title: 'Verify Compliance',
      description: 'Confirm successful resolution',
      icon: 'ShieldCheck'
    }
  ];

  useEffect(() => {
    if (currentStep === 0) {
      generateAISuggestions();
    }
  }, [currentStep]);

  const generateAISuggestions = async () => {
    try {
      setLoadingAI(true);
      const suggestions = await openaiComplianceService?.generateRemediationSteps([violation]);
      
      if (suggestions?.remediations?.[0]) {
        setAiSuggestions(suggestions?.remediations?.[0]);
        // Auto-select all immediate actions
        setSelectedActions(suggestions?.remediations?.[0]?.immediateActions || []);
      }
    } catch (error) {
      console.error('Failed to generate AI suggestions:', error);
      // Fallback to manual suggestions
      setAiSuggestions({
        violationType: violation?.violationType,
        immediateActions: [
          'Review affected employee records',
          'Calculate backpay amounts',
          'Update payroll system rates'
        ],
        correctionSteps: [
          'Identify all affected pay periods',
          'Recalculate wages at correct rates',
          'Process backpay in next payroll cycle',
          'Update employee contracts',
          'Verify compliance with Employment Act'
        ],
        timeline: '3-5 business days',
        preventiveMeasures: [
          'Implement automated compliance checks',
          'Set up rate change alerts',
          'Schedule quarterly compliance audits'
        ]
      });
      setSelectedActions(['Review affected employee records', 'Calculate backpay amounts']);
    } finally {
      setLoadingAI(false);
    }
  };

  const handleNext = () => {
    if (currentStep < steps?.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const toggleAction = (action) => {
    setSelectedActions(prev => 
      prev?.includes(action) 
        ? prev?.filter(a => a !== action)
        : [...prev, action]
    );
  };

  const validateRemediation = () => {
    setValidationResults({
      backpayCalculated: true,
      affectedEmployeesIdentified: true,
      complianceRulesVerified: true,
      estimatedCost: violation?.estimatedCost,
      processingTime: aiSuggestions?.timeline || '3-5 business days'
    });
  };

  const handleProcessRemediation = async () => {
    try {
      setProcessing(true);
      const remediationType = {
        minimum_wage_violation: 'minimum_wage_adjustment',
        overtime_rate_violation: 'overtime_recalculation',
        leave_policy_violation: 'leave_balance_correction'
      }?.[violation?.violationType];

      if (remediationType) {
        await violationDetectionService?.executeOneClickRemediation(violation?.id, remediationType);
        setCompleted(true);
        handleNext();
      }
    } catch (error) {
      console.error('Remediation failed:', error);
      alert('Remediation failed: ' + error?.message);
    } finally {
      setProcessing(false);
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'text-error',
      high: 'text-warning',
      medium: 'text-primary',
      low: 'text-success'
    };
    return colors?.[severity?.toLowerCase()] || 'text-muted-foreground';
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-card border border-border rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6 z-10">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon name="Sparkles" size={20} color="var(--color-primary)" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-xl text-foreground">AI-Powered Remediation Wizard</h2>
                <p className="text-xs text-muted-foreground">Intelligent step-by-step violation resolution</p>
              </div>
            </div>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
              <Icon name="X" size={24} />
            </button>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-between">
            {steps?.map((step, index) => (
              <div key={index} className="flex items-center">
                <div className={`flex flex-col items-center transition-all duration-300 ${
                  index <= currentStep ? 'opacity-100' : 'opacity-40'
                }`}>
                  <motion.div 
                    className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 transition-all ${
                      index < currentStep ? 'bg-success text-success-foreground' :
                      index === currentStep ? 'bg-primary text-primary-foreground shadow-lg scale-110': 'bg-muted text-muted-foreground'
                    }`}
                    animate={index === currentStep ? { scale: [1, 1.1, 1] } : {}}
                    transition={{ duration: 0.5, repeat: index === currentStep ? Infinity : 0, repeatDelay: 2 }}
                  >
                    {index < currentStep ? (
                      <Icon name="Check" size={20} />
                    ) : (
                      <Icon name={step?.icon} size={20} />
                    )}
                  </motion.div>
                  <p className="text-xs text-center text-muted-foreground max-w-[100px] leading-tight">{step?.title}</p>
                </div>
                {index < steps?.length - 1 && (
                  <div className={`w-16 h-1 mb-8 mx-2 rounded-full transition-all ${
                    index < currentStep ? 'bg-success' : 'bg-muted'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <AnimatePresence mode="wait">
            {/* Step 0: AI Analysis */}
            {currentStep === 0 && (
              <motion.div
                key="step-0"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <h3 className="font-heading font-semibold text-lg text-foreground mb-4 flex items-center gap-2">
                  <Icon name="Brain" size={20} color="var(--color-primary)" />
                  AI-Powered Analysis
                </h3>
                
                {loadingAI ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <Icon name="Loader2" size={64} color="var(--color-primary)" className="animate-spin mb-4" />
                    <p className="text-sm text-muted-foreground mb-2">Analyzing violation with GPT-5...</p>
                    <p className="text-xs text-muted-foreground">Generating intelligent remediation suggestions</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="p-4 bg-gradient-to-r from-primary/10 to-transparent border border-primary/20 rounded-lg">
                      <div className="flex items-start gap-3">
                        <Icon name="Sparkles" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-1" />
                        <div>
                          <p className="text-sm font-semibold text-foreground mb-2">AI Analysis Complete</p>
                          <p className="text-xs text-muted-foreground">
                            Generated {aiSuggestions?.immediateActions?.length || 0} immediate actions and 
                            {aiSuggestions?.correctionSteps?.length || 0} correction steps using advanced compliance intelligence.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-card border border-border rounded-lg">
                        <p className="text-xs text-muted-foreground mb-1">Violation Type</p>
                        <p className="text-sm font-semibold text-foreground">{violation?.violationType?.replace(/_/g, ' ')}</p>
                      </div>
                      <div className="p-4 bg-card border border-border rounded-lg">
                        <p className="text-xs text-muted-foreground mb-1">Severity</p>
                        <p className={`text-sm font-semibold ${getSeverityColor(violation?.severity)}`}>
                          {violation?.severity?.toUpperCase()}
                        </p>
                      </div>
                      <div className="p-4 bg-card border border-border rounded-lg">
                        <p className="text-xs text-muted-foreground mb-1">Affected Employees</p>
                        <p className="text-sm font-semibold text-foreground">{violation?.affectedCount}</p>
                      </div>
                      <div className="p-4 bg-card border border-border rounded-lg">
                        <p className="text-xs text-muted-foreground mb-1">Estimated Timeline</p>
                        <p className="text-sm font-semibold text-foreground">{aiSuggestions?.timeline || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            {/* Step 1: Review AI Suggestions */}
            {currentStep === 1 && (
              <motion.div
                key="step-1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <h3 className="font-heading font-semibold text-lg text-foreground mb-4 flex items-center gap-2">
                  <Icon name="FileSearch" size={20} color="var(--color-primary)" />
                  Review AI-Generated Suggestions
                </h3>

                <div className="space-y-4">
                  {/* Immediate Actions */}
                  <div className="p-4 bg-card border border-border rounded-lg">
                    <h4 className="font-semibold text-sm text-foreground mb-3 flex items-center gap-2">
                      <Icon name="Zap" size={16} color="var(--color-warning)" />
                      Immediate Actions (Auto-Selected)
                    </h4>
                    <div className="space-y-2">
                      {aiSuggestions?.immediateActions?.map((action, idx) => (
                        <label key={idx} className="flex items-start gap-3 p-3 bg-warning/5 border border-warning/20 rounded-lg cursor-pointer hover:bg-warning/10 transition-colors">
                          <input
                            type="checkbox"
                            checked={selectedActions?.includes(action)}
                            onChange={() => toggleAction(action)}
                            className="mt-1 w-4 h-4 text-primary rounded focus:ring-2 focus:ring-primary"
                          />
                          <span className="text-sm text-foreground flex-1">{action}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Correction Steps */}
                  <div className="p-4 bg-card border border-border rounded-lg">
                    <h4 className="font-semibold text-sm text-foreground mb-3 flex items-center gap-2">
                      <Icon name="ListChecks" size={16} color="var(--color-primary)" />
                      Detailed Correction Steps
                    </h4>
                    <ol className="space-y-2">
                      {aiSuggestions?.correctionSteps?.map((step, idx) => (
                        <li key={idx} className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg">
                          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary text-xs font-bold flex items-center justify-center">
                            {idx + 1}
                          </span>
                          <span className="text-sm text-foreground flex-1">{step}</span>
                        </li>
                      ))}
                    </ol>
                  </div>

                  {/* Preventive Measures */}
                  {aiSuggestions?.preventiveMeasures && (
                    <div className="p-4 bg-success/5 border border-success/20 rounded-lg">
                      <h4 className="font-semibold text-sm text-foreground mb-3 flex items-center gap-2">
                        <Icon name="Shield" size={16} color="var(--color-success)" />
                        Preventive Measures
                      </h4>
                      <ul className="space-y-2">
                        {aiSuggestions?.preventiveMeasures?.map((measure, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                            <Icon name="CheckCircle2" size={14} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                            <span>{measure}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Step 2: Validate & Approve */}
            {currentStep === 2 && (
              <motion.div
                key="step-2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <h3 className="font-heading font-semibold text-lg text-foreground mb-4 flex items-center gap-2">
                  <Icon name="CheckSquare" size={20} color="var(--color-primary)" />
                  Validate & Approve Corrections
                </h3>

                {!validationResults ? (
                  <div className="text-center py-8">
                    <Button
                      variant="primary"
                      size="lg"
                      onClick={validateRemediation}
                      iconName="Play"
                    >
                      Run Validation Checks
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="p-4 bg-success/10 border border-success/20 rounded-lg">
                      <div className="flex items-center gap-3 mb-4">
                        <Icon name="CheckCircle2" size={24} color="var(--color-success)" />
                        <div>
                          <p className="text-sm font-semibold text-success">Validation Successful</p>
                          <p className="text-xs text-muted-foreground">All checks passed - ready to proceed</p>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-card border border-border rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs text-muted-foreground">Backpay Calculated</p>
                          <Icon name="CheckCircle2" size={16} color="var(--color-success)" />
                        </div>
                        <p className="text-lg font-bold text-success">K{violation?.estimatedCost?.toFixed(2)}</p>
                      </div>
                      <div className="p-4 bg-card border border-border rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs text-muted-foreground">Processing Time</p>
                          <Icon name="Clock" size={16} color="var(--color-primary)" />
                        </div>
                        <p className="text-lg font-bold text-primary">{validationResults?.processingTime}</p>
                      </div>
                    </div>

                    <div className="p-4 bg-warning/5 border border-warning/20 rounded-lg">
                      <div className="flex items-start gap-3">
                        <Icon name="Info" size={20} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-semibold text-foreground mb-2">What will happen:</p>
                          <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                            <li>System will automatically recalculate affected payroll entries</li>
                            <li>Backpay of K{violation?.estimatedCost?.toFixed(2)} will be processed</li>
                            <li>Affected employees will be notified via email</li>
                            <li>Compliance records will be updated automatically</li>
                            <li>Audit trail will be generated for verification</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            {/* Step 3: Execute Remediation */}
            {currentStep === 3 && (
              <motion.div
                key="step-3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <h3 className="font-heading font-semibold text-lg text-foreground mb-4 flex items-center gap-2">
                  <Icon name="Zap" size={20} color="var(--color-primary)" />
                  Execute Automated Remediation
                </h3>
                
                <div className="flex flex-col items-center justify-center py-12">
                  {!completed ? (
                    <>
                      <Icon name="Loader2" size={64} color="var(--color-primary)" className="animate-spin mb-4" />
                      <p className="text-sm font-semibold text-foreground mb-2">Processing corrections...</p>
                      <p className="text-xs text-muted-foreground">This may take a few moments</p>
                    </>
                  ) : (
                    <>
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                      >
                        <Icon name="CheckCircle2" size={80} color="var(--color-success)" className="mb-4" />
                      </motion.div>
                      <p className="text-lg font-bold text-success mb-2">Remediation Completed!</p>
                      <p className="text-sm text-muted-foreground">All corrections have been applied successfully</p>
                    </>
                  )}
                </div>
              </motion.div>
            )}

            {/* Step 4: Verify Compliance */}
            {currentStep === 4 && (
              <motion.div
                key="step-4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <h3 className="font-heading font-semibold text-lg text-foreground mb-4 flex items-center gap-2">
                  <Icon name="ShieldCheck" size={20} color="var(--color-success)" />
                  Compliance Verified
                </h3>
                
                <div className="text-center py-8">
                  <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: 'spring', stiffness: 150, damping: 12 }}
                  >
                    <Icon name="Award" size={100} color="var(--color-success)" className="mx-auto mb-4" />
                  </motion.div>
                  <h4 className="font-heading font-bold text-2xl text-success mb-2">Compliance Restored</h4>
                  <p className="text-sm text-muted-foreground mb-6">
                    The violation has been successfully remediated and your organization is now fully compliant with PNG Employment Act.
                  </p>
                  
                  <div className="grid grid-cols-3 gap-4 max-w-2xl mx-auto mb-6">
                    <div className="p-4 bg-success/10 border border-success/20 rounded-lg">
                      <Icon name="DollarSign" size={24} color="var(--color-success)" className="mx-auto mb-2" />
                      <p className="text-xs text-muted-foreground mb-1">Backpay Processed</p>
                      <p className="text-lg font-bold text-success">K{violation?.estimatedCost?.toFixed(2)}</p>
                    </div>
                    <div className="p-4 bg-primary/10 border border-primary/20 rounded-lg">
                      <Icon name="Users" size={24} color="var(--color-primary)" className="mx-auto mb-2" />
                      <p className="text-xs text-muted-foreground mb-1">Employees Notified</p>
                      <p className="text-lg font-bold text-primary">{violation?.affectedCount}</p>
                    </div>
                    <div className="p-4 bg-success/10 border border-success/20 rounded-lg">
                      <Icon name="TrendingUp" size={24} color="var(--color-success)" className="mx-auto mb-2" />
                      <p className="text-xs text-muted-foreground mb-1">Compliance Score</p>
                      <p className="text-lg font-bold text-success">100%</p>
                    </div>
                  </div>

                  <div className="p-4 bg-gradient-to-r from-success/10 to-transparent border border-success/20 rounded-lg text-left">
                    <p className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                      <Icon name="FileCheck" size={16} color="var(--color-success)" />
                      Next Steps:
                    </p>
                    <ul className="space-y-1 text-sm text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <Icon name="Check" size={14} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                        <span>Audit trail has been generated and saved</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Icon name="Check" size={14} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                        <span>Preventive measures have been implemented</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Icon name="Check" size={14} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                        <span>Automated monitoring is now active</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 p-6 border-t border-border bg-card">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={handlePrevious}
              disabled={currentStep === 0 || processing || loadingAI}
              iconName="ChevronLeft"
            >
              Previous
            </Button>
            
            <div className="flex items-center gap-2">
              {currentStep === 2 && validationResults && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleProcessRemediation}
                  disabled={processing}
                  iconName={processing ? 'Loader2' : 'Zap'}
                >
                  {processing ? 'Processing...' : 'Execute Remediation'}
                </Button>
              )}
              {currentStep === 4 ? (
                <Button variant="success" size="sm" onClick={onClose} iconName="Check">
                  Complete
                </Button>
              ) : currentStep < 2 && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleNext}
                  disabled={loadingAI || (currentStep === 1 && selectedActions?.length === 0)}
                  iconName="ChevronRight"
                  iconPosition="right"
                >
                  Next
                </Button>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default EnhancedRemediationWizard;